import { router } from 'expo-router';
import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { ErrorRemovingLastFavoriteStoreModal } from '@fhs-legacy/frontend/src/components/modal-error-removing-last-favorite-store';
import { SwitchServiceModeDialog } from '@fhs-legacy/frontend/src/components/switch-service-mode-dialog';
import useAuth from '@fhs-legacy/frontend/src/hooks/auth/use-auth';
import { useFavoriteStore } from '@fhs-legacy/frontend/src/hooks/favorite-stores';
import { useDistanceText } from '@fhs-legacy/frontend/src/pages/store-locator/new-ui/store-card/hooks/use-distance-text';
import {
  formatRestaurantPhysicalAddress,
  formatRestaurantPhysicalAddressForAccessibility,
} from '@fhs-legacy/frontend/src/pages/store-locator/new-ui/store-card/utils/format-restaurant-physical-address';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';

import { useStoreCardOrderAction } from './hooks/use-store-card-order-action';
import { useStoreCardStatus } from './hooks/use-store-card-status';
import { SaveFavoritesLoginModal } from './save-favorites-login-modal';
import { StoreCardV2View } from './store-card.view';
import type { StoreCardBadge, StoreCardV2Props } from './types';

/**
 * Container for the v2 locator card. Data orchestration lives here so the view stays
 * presentational, matching the split used by the legacy card.
 */
export const StoreCardV2: React.VFC<StoreCardV2Props> = ({
  restaurant,
  isSelected,
  onSelect,
  isStoreLocatorFocusTarget,
  storeLocatorFocusRef,
  testID,
}) => {
  const { formatMessage } = useIntl();
  const { isAuthenticated } = useAuth();
  const enableFutureOrdering = useFlag('enable-future-ordering');
  const [saveFavoritesLoginOpen, setSaveFavoritesLoginOpen] = useState(false);

  const distanceText = useDistanceText({ restaurant });
  const status = useStoreCardStatus(restaurant);

  const {
    orderAction,
    unavailableMessage,
    isDisabled,
    storeClosedActionSheet,
    showChangeToPickUpDialog,
    dismissChangeToPickUpDialog,
    confirmChangeToPickUp,
  } = useStoreCardOrderAction(restaurant);

  const {
    favStoreLoading,
    isFavorite,
    toggleFavorite,
    hasErrorRemovingLastFavStore,
    onDismissErrorRemovingLastFavStoreModal,
  } = useFavoriteStore(restaurant._id ?? '', restaurant.number ?? '');

  const onFavoritePress = useCallback(() => {
    if (!isAuthenticated) {
      setSaveFavoritesLoginOpen(true);
      return;
    }
    toggleFavorite();
  }, [isAuthenticated, toggleFavorite]);

  // Details open as a pane of the locator, so this only adds a param to the route already on
  // screen — navigating would tear down the map and rebuild it on the way back.
  const onInfoPress = useCallback(() => {
    router.setParams({ storeId: restaurant._id });
  }, [restaurant._id]);

  const address = formatRestaurantPhysicalAddress(restaurant.physicalAddress);
  const statusLine = status.hoursText
    ? `${status.statusText} • ${status.hoursText}`
    : status.statusText;

  // Both reasons share the badge slot, and being unable to order at all outranks closing soon.
  const badge: StoreCardBadge | undefined = unavailableMessage
    ? { label: unavailableMessage, tone: 'error' }
    : status.badge
      ? { label: status.badge, tone: 'warning' }
      : undefined;

  const accessibilityLabel = [
    isSelected ? formatMessage({ id: 'selected' }) : undefined,
    restaurant.name,
    formatRestaurantPhysicalAddressForAccessibility(restaurant.physicalAddress) ?? address,
    badge?.label,
    statusLine,
  ]
    .filter(part => Boolean(part && String(part).trim()))
    .join(', ');

  // "Schedule Order" is only for a closed store that can still take a future order.
  // If mobile ordering isn't available at all, keep the disabled "Order Here" CTA.
  const isClosed = !status.isOpen;
  const canScheduleOrder =
    isClosed && enableFutureOrdering && status.supportsMobileOrdering && !unavailableMessage;
  const ctaLabel = canScheduleOrder ? 'Schedule Order' : formatMessage({ id: 'orderHere' });

  return (
    <>
      <StoreCardV2View
        testID={testID}
        accessibilityLabel={accessibilityLabel}
        address={address}
        badge={badge}
        ctaLabel={orderAction ? ctaLabel : undefined}
        ctaAccessibilityLabel={`${ctaLabel} at ${restaurant.name ?? ''}`}
        distanceText={distanceText}
        hoursText={status.hoursText}
        isCtaDisabled={
          isDisabled || !status.supportsMobileOrdering || (isClosed && !enableFutureOrdering)
        }
        isCtaLoading={Boolean(orderAction?.loading)}
        isFavorite={isFavorite}
        isSelected={isSelected}
        isStoreLocatorFocusTarget={isStoreLocatorFocusTarget}
        isUpdatingFavorites={favStoreLoading}
        name={restaurant.name}
        onCtaPress={orderAction?.onClick}
        onFavoritePress={onFavoritePress}
        onInfoPress={onInfoPress}
        onSelect={onSelect}
        statusText={status.statusText}
        storeLocatorFocusRef={storeLocatorFocusRef}
      />
      {storeClosedActionSheet}
      {showChangeToPickUpDialog && (
        <SwitchServiceModeDialog
          showDialog
          onConfirm={confirmChangeToPickUp}
          onDismiss={dismissChangeToPickUpDialog}
        />
      )}
      {hasErrorRemovingLastFavStore && (
        <ErrorRemovingLastFavoriteStoreModal
          isOpen={hasErrorRemovingLastFavStore}
          onDismiss={onDismissErrorRemovingLastFavStoreModal}
        />
      )}
      <SaveFavoritesLoginModal
        isOpen={saveFavoritesLoginOpen}
        onClose={() => setSaveFavoritesLoginOpen(false)}
      />
    </>
  );
};
